import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query'
import { BACKEND_API_URL, routesGuest, urlApi } from '../consts'

export const revokeToken = async () => {
  try {
    return await fetch(`${BACKEND_API_URL}${urlApi.logout}`, {
      method: 'POST',
      credentials: 'include',
      // Prevent the request from being cancelled on navigation.
      keepalive: true,
    })
  } catch (e) {
    console.error(e)
  }
}

export const logOut = async () => {
  await revokeToken()
  window.location.assign(routesGuest.ROOT.path)
}

/**
 * Базовый baseQuery с cookie-auth (HttpOnly cookies).
 * @baseUrl string - базовый урл
 */
export const fetchBaseQueryWithToken = (baseUrl: string) =>
  baseQueryWithReauth({
    baseUrl: baseUrl,
  })

type BaseQueryWithReauthArgs = {
  baseUrl: string
}

/**
 * Один общий refresh на все RTK-срезы: бэкенд ротирует refresh_token —
 * параллельные POST /auth/refresh отменяют «второй» токен и дают ложный 401.
 */
let cookieAuthRefreshCoordinator: Promise<boolean> | null = null

function coalesceCookieRefresh(runner: () => unknown): Promise<boolean> {
  if (!cookieAuthRefreshCoordinator) {
    cookieAuthRefreshCoordinator = Promise.resolve(runner())
      .then((r: any) => !r?.error)
      .finally(() => {
        cookieAuthRefreshCoordinator = null
      })
  }
  return cookieAuthRefreshCoordinator
}

const baseQueryWithReauth = ({ baseUrl }: BaseQueryWithReauthArgs) => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('accept', 'application/json')
      return headers
    },
  })

  return async (args: any, api: any, extraOptions: any) => {
    let result = await rawBaseQuery(args, api, extraOptions)

    const url = typeof args === 'string' ? args : args?.url
    const isAuthEndpoint =
      typeof url === 'string' &&
      (url.includes(urlApi.requestEmailCode) ||
        url.includes(urlApi.verifyEmailCode) ||
        url.includes(urlApi.login) ||
        url.includes(urlApi.register) ||
        url.includes(urlApi.refresh) ||
        url.includes(urlApi.logout))

    if (result?.error?.status === 401 && !isAuthEndpoint) {
      const refreshedOk = await coalesceCookieRefresh(() =>
        rawBaseQuery({ url: urlApi.refresh, method: 'POST' }, api, extraOptions)
      )

      if (refreshedOk) {
        result = await rawBaseQuery(args, api, extraOptions)
      }
    }

    return result
  }
}

export const extractResponseData = (response: any) => {
  // Если в ответе есть поле data — берем его, если нет — берем весь ответ
  return response && response.data ? response.data : response;
}

export const extractResponseError = (response: any): unknown => {
  return response as any
}

export const extractApiErrorMessage = (error: unknown): string | null => {
  const payload = error as any
  const directMessage =
    typeof payload?.data?.message === 'string' ? payload.data.message.trim() : ''
  if (directMessage.length > 0) {
    return directMessage
  }

  const errors = payload?.data?.errors
  if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors as Record<string, unknown>)) {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim()
      }
      if (Array.isArray(value)) {
        const firstText = value.find(
          (item) => typeof item === 'string' && item.trim().length > 0
        )
        if (typeof firstText === 'string') {
          return firstText.trim()
        }
      }
    }
  }

  const fallbackMessage =
    typeof payload?.message === 'string' ? payload.message.trim() : ''
  return fallbackMessage.length > 0 ? fallbackMessage : null
}

export function isProjectAccessRestrictedError(error: unknown): boolean {
  const payload = error as { status?: number; data?: { code?: unknown } } | null
  if (payload?.status !== 403) return false
  return payload?.data?.code === 'ProjectAccessRestricted'
}

export function isSocialOrderAccessRestrictedError(error: unknown): boolean {
  const payload = error as { status?: number; data?: { code?: unknown } } | null
  if (payload?.status !== 403) return false
  return payload?.data?.code === 'SocialOrderAccessRestricted'
}

export function isGrowthToolAccessRestrictedError(error: unknown): boolean {
  const payload = error as { status?: number; data?: { code?: unknown } } | null
  if (payload?.status !== 403) return false
  return payload?.data?.code === 'GrowthToolAccessRestricted'
}